import React, { useState, useEffect, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";

// Thèmes pro (inchangés)
const themes = {
  light: {
    sidebarBg: "#f6f8fa",
    color: "#23253a",
    active: "#2563eb",
    accent: "#fa709a",
    border: "1.5px solid #e5e7eb",
    navHover: "#eceff1",
    avatarRing: "#fee140",
  },
  dark: {
    sidebarBg: "#191b24",
    color: "#f3f4f6",
    active: "#fee140",
    accent: "#fa709a",
    border: "1.5px solid #23253a",
    navHover: "#24253c",
    avatarRing: "#fa709a",
  },
};

const getInitials = () => "AD";

const Sidebar = ({ themeKey = "light", onToggleTheme }) => {
  const [sidebarOpen, setSidebarOpen] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 992 : true
  );
  const [avatarMenu, setAvatarMenu] = useState(false);
  const history = useHistory();

  const theme = themes[themeKey];
  const bgColor = theme.avatarRing;

  useEffect(() => {
    const handleResize = () => setSidebarOpen(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    window.location.href = "/admin/logout";
  };

  const isActive = (path) => history.location.pathname === path;

  // --- Unpaid ---
  const unpaidLinks = useMemo(
    () => [
      { to: "/admin/dashboard", icon: "fas fa-users", label: "Clients" },
      { to: "/admin/settings", icon: "fas fa-file-alt", label: "Demandes" },
      { to: "/admin/annulations", icon: "fas fa-ban", label: "Annulations" },
      { to: "/admin/demandesaccepted", icon: "fas fa-check-circle", label: "Demandes Acceptées" },
      { to: "/admin/demandesrefused", icon: "fas fa-times-circle", label: "Demandes Refusées" },
      { to: "/admin/temoignages", icon: "fas fa-comment-dots", label: "Témoignages" },
      { to: "/admin/images", icon: "fas fa-id-card", label: "Dossier images" },
    ],
    []
  );

  // --- Banque ---
  const banqueLinks = useMemo(
    () => [
      { to: "/admin/bank/createuser", icon: "fas fa-user-plus", label: "Créer utilisateur" },
      { to: "/admin/banque/users", icon: "fas fa-users-cog", label: "Gérer utilisateurs" },
      { to: "/admin/banque/virement", icon: "fas fa-exchange-alt", label: "Faire un virement" },
      { to: "/admin/banque/transferts", icon: "fas fa-random", label: "Gérer virements" },
      { to: "/admin/banque/virementsvalides", icon: "fas fa-check", label: "Virements validés" },
      { to: "/admin/banque/validatecardsrequests", icon: "fas fa-times", label: "Demandes de Cartes" },
    ],
    []
  );

  // --- Mail ---
  const mailLinks = useMemo(
    () => [{ to: "/admin/email", icon: "fas fa-envelope", label: "Envoi d'Emails" }],
    []
  );

  const sectionHasActive = (links) => links.some((l) => isActive(l.to));

  // ✅ Accordéon: une seule section ouverte à la fois
  const initialOpenKey = useMemo(() => {
    if (sectionHasActive(unpaidLinks)) return "unpaid";
    if (sectionHasActive(banqueLinks)) return "banque";
    if (sectionHasActive(mailLinks)) return "mail";
    return "unpaid"; // défaut
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [openSection, setOpenSection] = useState(initialOpenKey);

  useEffect(() => {
    if (sectionHasActive(unpaidLinks)) setOpenSection("unpaid");
    else if (sectionHasActive(banqueLinks)) setOpenSection("banque");
    else if (sectionHasActive(mailLinks)) setOpenSection("mail");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);

  const toggleSection = (key) => {
    setOpenSection((prev) => (prev === key ? "" : key)); // re-click => ferme
  };

  const unpaidOpen = openSection === "unpaid";
  const banqueOpen = openSection === "banque";
  const mailOpen = openSection === "mail";

  const styles = {
    sidebar: {
      width: 285,
      height: "100vh",
      position: "fixed",
      left: 0,
      top: 0,
      zIndex: 1300,
      background: theme.sidebarBg,
      color: theme.color,
      borderRight: theme.border,
      padding: "0 0 2.5rem 0",
      boxShadow: "2px 0 18px rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      backdropFilter: "blur(8px)",
      transition: "transform 0.3s cubic-bezier(.7,0,.3,1), background 0.24s",
      willChange: "transform",
    },
    logo: {
      textDecoration: "none",
      fontWeight: 700,
      fontSize: "1.25rem",
      letterSpacing: "1.1px",
      color: theme.active,
      marginLeft: "1.7rem",
    },
    avatarBlock: {
      display: "flex",
      alignItems: "center",
      gap: 16,
      padding: "22px 0 0 1.8rem",
    },
    avatar: {
      width: 48,
      height: 48,
      borderRadius: "50%",
      background: bgColor,
      color: "#fff",
      fontWeight: 700,
      fontSize: "1.5rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 10px rgba(100,100,100,0.12)",
      border: `3px solid ${theme.avatarRing}`,
      cursor: "pointer",
    },
    userInfo: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    avatarName: {
      fontWeight: 700,
      fontSize: 17,
      color: theme.accent,
      lineHeight: 1.1,
    },
    avatarEmail: {
      fontSize: 13,
      color: themeKey === "dark" ? "#ccc" : "#8a8a8a",
    },
    avatarMenu: {
      position: "absolute",
      top: 67,
      left: 76,
      background: theme.sidebarBg,
      minWidth: "170px",
      borderRadius: "12px",
      boxShadow: "0 10px 32px rgba(80,60,120,0.14)",
      border: theme.border,
      padding: "0.6rem 0",
      zIndex: 10,
    },
    menuItem: {
      padding: "11px 20px",
      fontWeight: 500,
      fontSize: 15,
      cursor: "pointer",
      transition: "background 0.13s",
      color: "inherit",
    },
    menuItemDanger: { color: "#ff4b4b" },
    navLink: {
      transition: "all 0.21s",
      borderRadius: "7px",
      margin: "3px 0",
      padding: "0.7rem 1.6rem",
      color: theme.color,
      textDecoration: "none",
      display: "flex",
      alignItems: "center",
      fontWeight: 500,
      fontSize: 16,
      gap: "12px",
    },
    activeLink: {
      fontWeight: 700,
      background: theme.active + "13",
      color: theme.active,
      borderLeft: `4.5px solid ${theme.accent}`,
      marginLeft: "-8px",
    },
    themeBtn: {
      margin: "10px 0 0 1.7rem",
      borderRadius: "7px",
      fontWeight: 500,
      background: theme.active + "14",
      color: theme.active,
      border: "none",
      padding: "6px 14px",
      cursor: "pointer",
      fontSize: 15,
    },
    footer: {
      margin: "2rem 0 0 0",
      textAlign: "center",
      fontSize: 13,
      color: theme.color,
      opacity: 0.34,
    },
    hamburgerBtn: {
      zIndex: 1500,
      position: "fixed",
      top: 17,
      left: 17,
      width: 48,
      height: 48,
      background: theme.sidebarBg,
      border: theme.border,
      borderRadius: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 8px rgba(100,100,100,0.09)",
      color: theme.color,
      cursor: "pointer",
    },

    sectionHeader: {
      transition: "all 0.21s",
      borderRadius: "7px",
      margin: "6px 0 3px 0",
      padding: "0.7rem 1.6rem",
      color: theme.color,
      textDecoration: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontWeight: 500,
      fontSize: 16,
      cursor: "pointer",
      userSelect: "none",
    },
    caret: {
      transition: "transform 0.2s ease",
      fontSize: 14,
      opacity: 0.8,
    },
    sectionBody: {
      overflow: "hidden",
      transition: "max-height 0.28s ease",
    },
  };

  return (
    <>
      {/* Hamburger mobile */}
      {typeof window !== "undefined" && window.innerWidth < 992 && !sidebarOpen && (
        <button style={styles.hamburgerBtn} className="d-lg-none" onClick={() => setSidebarOpen(true)}>
          <i className="fas fa-bars"></i>
        </button>
      )}

      {/* Sidebar */}
      <div
        style={{
          ...styles.sidebar,
          ...(sidebarOpen ? { transform: "translateX(0)" } : { transform: "translateX(-100%)" }),
        }}
        className="sidebar"
      >
        {/* Header */}
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Link to="/" style={styles.logo}>
              <i className="fas fa-lock me-2"></i>
              AidFoundation Admin
            </Link>
            <button
              className="d-lg-none btn btn-link text-dark p-0"
              onClick={() => setSidebarOpen(false)}
              style={{ fontSize: 26, marginRight: 20 }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Avatar */}
          <div style={styles.avatarBlock}>
            <div style={{ position: "relative" }}>
              <div style={styles.avatar} onClick={() => setAvatarMenu(!avatarMenu)} title="Admin">
                {getInitials()}
              </div>

              {avatarMenu && (
                <div style={styles.avatarMenu}>
                  <div
                    style={styles.menuItem}
                    onClick={() => {
                      setAvatarMenu(false);
                      onToggleTheme && onToggleTheme();
                    }}
                  >
                    <i className="fas fa-adjust me-2"></i>
                    {themeKey === "light" ? "Thème sombre" : "Thème clair"}
                  </div>

                  <div style={{ ...styles.menuItem, ...styles.menuItemDanger }} onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt me-2"></i> Déconnexion
                  </div>
                </div>
              )}
            </div>

            <div style={styles.userInfo}>
              <span style={styles.avatarName}>Bienvenue Admin</span>
              <span style={styles.avatarEmail}>admin@unpaidfinancial.com</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div style={{ paddingLeft: 0, marginTop: "1.2rem" }}>
          {/* Unpaid */}
          <div
            role="button"
            aria-expanded={unpaidOpen}
            onClick={() => toggleSection("unpaid")}
            style={{
              ...styles.sectionHeader,
              background: sectionHasActive(unpaidLinks) ? theme.active + "13" : "transparent",
              color: sectionHasActive(unpaidLinks) ? theme.active : theme.color,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <i className="fas fa-layer-group"></i>
              AidFoundation
            </span>
            <i className="fas fa-chevron-right" style={{ ...styles.caret, transform: `rotate(${unpaidOpen ? 90 : 0}deg)` }} />
          </div>
          <div style={{ ...styles.sectionBody, maxHeight: unpaidOpen ? 1000 : 0 }}>
            <ul className="nav flex-column mb-2" style={{ paddingLeft: 0 }}>
              {unpaidLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    style={{ ...styles.navLink, ...(isActive(link.to) && styles.activeLink) }}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <i className={link.icon}></i>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Banque */}
          <div
            role="button"
            aria-expanded={banqueOpen}
            onClick={() => toggleSection("banque")}
            style={{
              ...styles.sectionHeader,
              background: sectionHasActive(banqueLinks) ? theme.active + "13" : "transparent",
              color: sectionHasActive(banqueLinks) ? theme.active : theme.color,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <i className="fas fa-university"></i>
              Banque
            </span>
            <i className="fas fa-chevron-right" style={{ ...styles.caret, transform: `rotate(${banqueOpen ? 90 : 0}deg)` }} />
          </div>
          <div style={{ ...styles.sectionBody, maxHeight: banqueOpen ? 600 : 0 }}>
            <ul className="nav flex-column mb-2" style={{ paddingLeft: 0 }}>
              {banqueLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    style={{ ...styles.navLink, ...(isActive(link.to) && styles.activeLink) }}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <i className={link.icon}></i>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Mail */}
          <div
            role="button"
            aria-expanded={mailOpen}
            onClick={() => toggleSection("mail")}
            style={{
              ...styles.sectionHeader,
              background: sectionHasActive(mailLinks) ? theme.active + "13" : "transparent",
              color: sectionHasActive(mailLinks) ? theme.active : theme.color,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <i className="fas fa-envelope"></i>
              Mail
            </span>
            <i className="fas fa-chevron-right" style={{ ...styles.caret, transform: `rotate(${mailOpen ? 90 : 0}deg)` }} />
          </div>
          <div style={{ ...styles.sectionBody, maxHeight: mailOpen ? 220 : 0 }}>
            <ul className="nav flex-column mb-2" style={{ paddingLeft: 0 }}>
              {mailLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    style={{ ...styles.navLink, ...(isActive(link.to) && styles.activeLink) }}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <i className={link.icon}></i>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bouton Thème */}
        <button style={styles.themeBtn} onClick={onToggleTheme}>
          <i className="fas fa-adjust me-2"></i>
          {themeKey === "light" ? "Mode sombre" : "Mode clair"}
        </button>

        <div style={styles.footer}>
          <i className="fas fa-shield-alt me-2"></i>
          AidFoundation Admin Panel
        </div>
      </div>

      {/* Overlay mobile */}
      {typeof window !== "undefined" && window.innerWidth < 992 && sidebarOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            backgroundColor: "rgba(0,0,0,0.33)",
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Responsive CSS */}
      <style>
        {`
        @media (min-width: 992px) {
          .sidebar { transform: translateX(0) !important; }
          .d-lg-none { display: none !important; }
        }
        .nav-link:hover:not(.active) {
          background-color: ${theme.navHover} !important;
          color: ${theme.accent} !important;
          transform: translateX(5px) scale(1.05);
        }
        @media (max-width: 991.98px) { .sidebar { width: 85vw !important; } }
        @media (max-width: 575.98px) { .sidebar { width: 98vw !important; } }
        `}
      </style>
    </>
  );
};

export default Sidebar;
